import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

import { policyAcceptanceStateSchema, signerCapacitySchema } from './policy-compliance-enums'

export const PolicyEvidenceErrorCode = {
  SELF_APPROVAL_REFUSED: 'SELF_APPROVAL_REFUSED',
  APPROVER_NOT_AUTHORISED: 'APPROVER_NOT_AUTHORISED',
  APPROVER_PIN_NOT_SET: 'APPROVER_PIN_NOT_SET',
  APPROVER_PIN_INVALID: 'APPROVER_PIN_INVALID',
  APPROVER_PIN_LOCKED: 'APPROVER_PIN_LOCKED',
  CURRENT_PIN_REQUIRED: 'CURRENT_PIN_REQUIRED',
  RECENT_LOGIN_REQUIRED: 'RECENT_LOGIN_REQUIRED',
  CLIENT_NOT_AT_SITE: 'CLIENT_NOT_AT_SITE',
  REVISION_NOT_FOUND: 'REVISION_NOT_FOUND',
  SIGNED_ON_IN_FUTURE: 'SIGNED_ON_IN_FUTURE',
  SIGNER_CAPACITY_INVALID: 'SIGNER_CAPACITY_INVALID',
  SIGNER_NOT_FOUND: 'SIGNER_NOT_FOUND',
  SCAN_INVALID: 'SCAN_INVALID',
  SCAN_SOURCE_REFUSED: 'SCAN_SOURCE_REFUSED',
  TRUSTED_SOURCE_NOT_PERMITTED: 'TRUSTED_SOURCE_NOT_PERMITTED',
  BATCH_NOT_FOUND: 'BATCH_NOT_FOUND',
} as const
export type PolicyEvidenceErrorCode =
  (typeof PolicyEvidenceErrorCode)[keyof typeof PolicyEvidenceErrorCode]
export const policyEvidenceErrorCodeSchema = z.enum(PolicyEvidenceErrorCode).register(asEnumType, {
  name: 'PolicyEvidenceErrorCode',
  description: 'Why a paper form, an approval PIN or a policy evidence import was refused',
})

export const APPROVAL_PIN_MIN_LENGTH = 4
export const APPROVAL_PIN_MAX_LENGTH = 6
export const APPROVAL_PIN_MAX_FAILURES = 5
export const APPROVAL_PIN_FAILURE_WINDOW_MINUTES = 15
export const APPROVAL_PIN_LOCK_MINUTES = 15
export const APPROVAL_PIN_RECENT_LOGIN_MINUTES = 5
export const APPROVAL_PIN_PATTERN = new RegExp(
  `^\\d{${APPROVAL_PIN_MIN_LENGTH},${APPROVAL_PIN_MAX_LENGTH}}$`
)

export const paperPolicyFormSlotInputSchema = z
  .object({
    policyRevisionId: z.uuid(),
    signerCapacity: signerCapacitySchema,
    signerId: z.uuid(),
    signerName: z.string().trim().min(1).max(255),
    signedOn: z.iso.date(),
  })
  .register(asObjectType, { name: 'PaperPolicyFormSlotInput' })

export const recordPaperPolicyFormInputSchema = z
  .object({
    siteId: z.uuid(),
    clientId: z.uuid(),
    scanUploadId: z.uuid(),
    slots: z.array(paperPolicyFormSlotInputSchema).min(1).max(20),
    approverStaffUserId: z.uuid(),
    approverPin: z.string().regex(APPROVAL_PIN_PATTERN),
    attestationRevisionId: z.uuid().nullish(),
  })
  .register(asObjectType, { name: 'RecordPaperPolicyFormInput' })

export const policyEvidenceAcceptanceResultSchema = z
  .object({
    slotIndex: z.number().int().nonnegative(),
    agreementId: z.uuid(),
    policyRevisionId: z.uuid(),
    signerCapacity: signerCapacitySchema,
    acceptanceState: policyAcceptanceStateSchema,
  })
  .register(asObjectType, { name: 'PolicyEvidenceAcceptanceResult' })

export const paperPolicyFormResultSchema = z
  .object({
    paperRecordId: z.uuid(),
    approvedByStaffUserId: z.uuid(),
    acceptances: z.array(policyEvidenceAcceptanceResultSchema),
  })
  .register(asObjectType, { name: 'PaperPolicyFormResult' })

export const paperScanUploadInputSchema = z
  .object({ siteId: z.uuid(), clientId: z.uuid() })
  .register(asObjectType, { name: 'PaperScanUploadInput' })

export const paperScanUploadHeaderSchema = z
  .object({ name: z.string(), value: z.string() })
  .register(asObjectType, { name: 'PaperScanUploadHeader' })

export const paperScanUploadTicketSchema = z
  .object({
    uploadId: z.uuid(),
    uploadUrl: z.string(),
    headers: z.array(paperScanUploadHeaderSchema),
    expiresAt: z.date(),
  })
  .register(asObjectType, { name: 'PaperScanUploadTicket' })

export const setMyApprovalPinInputSchema = z
  .object({
    pin: z.string().regex(APPROVAL_PIN_PATTERN),
    currentPin: z.string().regex(APPROVAL_PIN_PATTERN).nullish(),
  })
  .register(asObjectType, { name: 'SetMyApprovalPinInput' })

export const verifyMyApprovalPinInputSchema = z
  .object({
    pin: z.string().regex(APPROVAL_PIN_PATTERN),
    siteId: z.uuid().nullish(),
  })
  .register(asObjectType, { name: 'VerifyMyApprovalPinInput' })

export const approvalPinStatusSchema = z
  .object({
    isSet: z.boolean(),
    setAt: z.date().nullish(),
    lockedUntil: z.date().nullish(),
  })
  .register(asObjectType, { name: 'ApprovalPinStatus' })

export const paperFormApproversInputSchema = z
  .object({ siteId: z.uuid() })
  .register(asObjectType, { name: 'PaperFormApproversInput' })

export const paperFormApproverSchema = z
  .object({
    staffUserId: z.uuid(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
  })
  .register(asObjectType, { name: 'PaperFormApprover' })

export const policyEvidenceImportRowSchema = z.object({
  rowIndex: z.number().int().nonnegative(),
  clientId: z.uuid(),
  policyRevisionId: z.uuid(),
  signerCapacity: signerCapacitySchema,
  signerId: z.uuid(),
  signerName: z.string().trim().min(1).max(255),
  signedOn: z.iso.date(),
  scanUrl: z.url().nullish(),
})

export const policyEvidenceTrustedSourceInputSchema = z.object({
  sourceSystem: z.string().trim().min(1).max(100),
  sourceEvidenceRetained: z.literal(true),
  attestationRevisionId: z.uuid(),
})

export const PolicyEvidenceImportRowOutcome = {
  IMPORTED: 'IMPORTED',
  UNVERIFIED_HISTORICAL: 'UNVERIFIED_HISTORICAL',
  DUPLICATE: 'DUPLICATE',
  REJECTED: 'REJECTED',
} as const
export type PolicyEvidenceImportRowOutcome =
  (typeof PolicyEvidenceImportRowOutcome)[keyof typeof PolicyEvidenceImportRowOutcome]
export const policyEvidenceImportRowOutcomeSchema = z
  .enum(PolicyEvidenceImportRowOutcome)
  .register(asEnumType, {
    name: 'PolicyEvidenceImportRowOutcome',
    description: 'What happened to one row of a signed-policy import',
  })

export const voidPolicyImportBatchInputSchema = z
  .object({ siteId: z.uuid(), batchId: z.uuid(), reason: z.string().trim().min(1).max(500) })
  .register(asObjectType, { name: 'VoidPolicyImportBatchInput' })

export const voidPolicyImportRowInputSchema = z
  .object({
    siteId: z.uuid(),
    batchId: z.uuid(),
    agreementId: z.uuid(),
    reason: z.string().trim().min(1).max(500),
  })
  .register(asObjectType, { name: 'VoidPolicyImportRowInput' })

export const voidPolicyImportResultSchema = z
  .object({ batchId: z.uuid(), voidedAgreements: z.number().int().nonnegative() })
  .register(asObjectType, { name: 'VoidPolicyImportResult' })

export type PaperPolicyFormSlotInput = z.infer<typeof paperPolicyFormSlotInputSchema>
export type RecordPaperPolicyFormInput = z.infer<typeof recordPaperPolicyFormInputSchema>
export type PolicyEvidenceAcceptanceResult = z.infer<typeof policyEvidenceAcceptanceResultSchema>
export type PaperPolicyFormResult = z.infer<typeof paperPolicyFormResultSchema>
export type PaperScanUploadInput = z.infer<typeof paperScanUploadInputSchema>
export type PaperScanUploadTicket = z.infer<typeof paperScanUploadTicketSchema>
export type SetMyApprovalPinInput = z.infer<typeof setMyApprovalPinInputSchema>
export type VerifyMyApprovalPinInput = z.infer<typeof verifyMyApprovalPinInputSchema>
export type ApprovalPinStatus = z.infer<typeof approvalPinStatusSchema>
export type PaperFormApproversInput = z.infer<typeof paperFormApproversInputSchema>
export type PaperFormApprover = z.infer<typeof paperFormApproverSchema>
export type PolicyEvidenceImportRow = z.infer<typeof policyEvidenceImportRowSchema>
export type PolicyEvidenceTrustedSourceInput = z.infer<
  typeof policyEvidenceTrustedSourceInputSchema
>
export type VoidPolicyImportBatchInput = z.infer<typeof voidPolicyImportBatchInputSchema>
export type VoidPolicyImportRowInput = z.infer<typeof voidPolicyImportRowInputSchema>
export type VoidPolicyImportResult = z.infer<typeof voidPolicyImportResultSchema>
